"use client";

import Image from "next/image";
// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import {
  ArrowUpRight,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import { z } from "zod";

// Clearing a FILE/INPUT field in admin persists an explicit `null` (not ""), and
// nullable DB columns come back as `null` too — coalesce instead of failing the
// parse, which would blank out the entire section.
const str = (def = "") =>
  z
    .string()
    .nullish()
    .transform((v) => v ?? def);

const num = (def = 0) =>
  z.preprocess((v) => {
    if (v == null || v === "") return def;
    const n = Number(v);
    return Number.isNaN(n) ? def : n;
  }, z.number());

// A missing/null metadata array, or a stray null row inside it, must not fail
// the whole section parse.
const rows = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess(
    (v) => (Array.isArray(v) ? v.filter((r) => r && typeof r === "object") : []),
    z.array(schema),
  );

const FeeRowSchema = z.object({
  meta_priority: num(0),
  meta_plan_label: str(),
  meta_student_type: str(),
  meta_scholarship_note: str(),
  meta_emi_image: str(),
  meta_emi_image_alt: str("No Cost EMI"),
  meta_emi_prefix_label: str("Starts at Just"),
  meta_emi_amount: str(),
  meta_total_fee_label: str("Total Fee"),
  meta_markup_fee: str(),
  meta_total_fee_amount: str(),
  meta_semester_label: str("Per Semester"),
  meta_semester_amount: str(),
  meta_annual_label: str("Per Annum"),
  meta_annual_amount: str(),
  meta_other_fees: str(),
  meta_eligibility: str(),
  meta_cta_url: str(),
});

const CertRowSchema = z.object({
  meta_priority: num(0),
  meta_plan_label: str(),
  meta_accordion_title: str(),
  meta_cert_image: str(),
  meta_cert_alt: str("Certificate"),
});

const SectionSchema = z.object({
  title: str(),
  title_start_color: str("#171719"),
  title_end_color: str("#E15518"),
  subtitle: str(),
  subtitle_color: str("#373737"),
  pre_title: str(),
  description: str(),
  desktop_background_color: str("#FFFFFF"),
  button_text: str("Apply Now"),
  button_color: str("#EF4444"),
  button_text_color: str("#FFFFFF"),
  button_is_cta: z.union([z.boolean(), z.number()]).nullish(),
  card_hover_background_color: str("#EF4444"),
  gradient_color_1: str("#1D1D1F"),
  gradient_color_2: str("#B5460A"),
  metadata: rows(FeeRowSchema),
  metadata_1: rows(CertRowSchema),
  status: z.union([z.boolean(), z.number()]).nullish(),
});

type CertRow = z.infer<typeof CertRowSchema>;

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// Rich-text editor output is HTML; plain admin input is not. Render HTML as-is,
// otherwise run it through the `{highlight}` formatter (mirrors FeePlan).
function isHtml(text: string): boolean {
  return /<[a-z][\s\S]*>/i.test(text);
}

// Wrap any `{phrase}` in a colored span (mirrors FeePlan's formatSectionTitle).
function highlight(text: string, color: string): string {
  if (!text) return "";
  return escapeHtml(text).replace(
    /\{([^}]+)\}/g,
    `<span style="color:${color}">$1</span>`,
  );
}

function normalizeHref(href: string): string {
  const h = href.trim();
  if (!h) return "#";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

interface AccordionGroup {
  title: string;
  images: { src: string; alt: string }[];
}

// Group certificate rows by accordion title (preserving priority order); each
// group's images become a carousel.
function groupCertificates(rows: CertRow[]): AccordionGroup[] {
  const ordered = [...rows].sort(
    (a, b) => (a.meta_priority || 0) - (b.meta_priority || 0),
  );
  const groups: AccordionGroup[] = [];
  const byTitle = new Map<string, AccordionGroup>();
  for (const row of ordered) {
    const title = (row.meta_accordion_title || "").trim();
    const src = (row.meta_cert_image || "").trim();
    if (!title && !src) continue;
    let group = byTitle.get(title);
    if (!group) {
      group = { title, images: [] };
      byTitle.set(title, group);
      groups.push(group);
    }
    if (src) {
      group.images.push({ src, alt: row.meta_cert_alt?.trim() || "Certificate" });
    }
  }
  return groups;
}

export default function ProgramBenefitsFeePlan({ data }: { data: any }) {
  const ref = useRef<HTMLElement>(null);
  const dispatch = useDispatch();
  const raw = data?.data ?? data ?? {};
  const parsed = useMemo(() => SectionSchema.safeParse(raw), [raw]);
  const section = parsed.success ? parsed.data : null;

  const [openAccordionIndex, setOpenAccordionIndex] = useState<number>(0);
  const [carouselIndices, setCarouselIndices] = useState<{ [key: number]: number }>(
    {},
  );
  const [selectedPlan, setSelectedPlan] = useState<number>(0);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "program_benefits_fee_plan_6");
  //   }
  // }, []);

  // Switching the plan changes which certificates show — reset accordion/carousel.
  useEffect(() => {
    setOpenAccordionIndex(0);
    setCarouselIndices({});
  }, [selectedPlan]);

  const handleApplyClick = useCallback(() => {
    ctaClick({
      section: "program_benefits_fee_plan_6",
      component: "cta_apply",
      label: section?.button_text?.trim() || "apply",
      step: "1",
      entity: "program_benefits_6",
      parentEntity: "program_benefits",
    });
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
  }, [dispatch, section?.button_text]);

  const handlePrevCertificate = useCallback((groupIndex: number, total: number) => {
    setCarouselIndices((prev) => {
      const current = prev[groupIndex] || 0;
      return { ...prev, [groupIndex]: current === 0 ? total - 1 : current - 1 };
    });
  }, []);

  const handleNextCertificate = useCallback((groupIndex: number, total: number) => {
    setCarouselIndices((prev) => {
      const current = prev[groupIndex] || 0;
      return { ...prev, [groupIndex]: current === total - 1 ? 0 : current + 1 };
    });
  }, []);

  if (!section) return null;

  const isOff =
    section.status === false ||
    section.status === 0 ||
    String(section.status) === "0";
  if (isOff) return null;

  const feeRows = [...(section.metadata ?? [])].sort(
    (a, b) => (a.meta_priority || 0) - (b.meta_priority || 0),
  );
  const showPlanToggle = feeRows.length > 1;
  const feeRow = feeRows[selectedPlan] ?? feeRows[0] ?? FeeRowSchema.parse({});

  const accent = section.title_end_color?.trim() || "#E15518";
  const amountBoxBg = section.card_hover_background_color?.trim() || "#EF4444";
  const feeCardGradient = `linear-gradient(82.66deg, ${
    section.gradient_color_1?.trim() || "#1D1D1F"
  } 21.45%, ${section.gradient_color_2?.trim() || "#B5460A"} 137.95%)`;

  const hasEmi =
    Boolean(feeRow.meta_emi_amount?.trim()) &&
    Boolean(feeRow.meta_emi_prefix_label?.trim());
  const hasTotalFee = Boolean(feeRow.meta_total_fee_amount?.trim());
  const hasSemester = Boolean(feeRow.meta_semester_amount?.trim());
  const hasAnnual = Boolean(feeRow.meta_annual_amount?.trim());
  const hasAmountBoxes = hasSemester || hasAnnual;
  const hasFeeStructure = hasTotalFee || hasAmountBoxes;

  const notes = (feeRow.meta_other_fees || "")
    .split(/\r?\n/)
    .map((n) => n.trim())
    .filter(Boolean);

  const ctaHrefRaw = (feeRow.meta_cta_url || "").trim();
  const ctaHrefNormalized = ctaHrefRaw ? normalizeHref(ctaHrefRaw) : "";
  const buttonIsCta = isCmsShowFlagOn(section.button_is_cta, true);
  const opensLeadPopup = buttonIsCta || !ctaHrefNormalized;

  // When the plan toggle is active, show only certificate rows tagged for the
  // selected plan (a blank plan label = shared across all plans).
  const selectedPlanLabel = (feeRow.meta_plan_label || "").trim();
  const certRows = (section.metadata_1 ?? []).filter((row) => {
    if (!showPlanToggle) return true;
    const label = (row.meta_plan_label || "").trim();
    return label === "" || label === selectedPlanLabel;
  });
  const certificateGroups = groupCertificates(certRows);
  const showCertificates =
    Boolean(section.pre_title?.trim()) || certificateGroups.length > 0;

  const btnStyle = {
    backgroundColor: section.button_color || "#EF4444",
    color: section.button_text_color || "#FFFFFF",
  };

  const toggleAccordion = (index: number) =>
    setOpenAccordionIndex((prev) => (prev === index ? -1 : index));

  // The dark fee card content (EMI badge + EMI line + total fee), shared between
  // the desktop and mobile layouts.
  const renderFeeCardInner = (variant: "desktop" | "mobile") => (
    <>
      {feeRow.meta_emi_image?.trim() ? (
        <div
          className={cn(
            "absolute",
            variant === "desktop" ? "top-[-1px] left-[-40px]" : "top-[20px] left-[6px]",
          )}
        >
          <Image
            src={feeRow.meta_emi_image}
            alt={feeRow.meta_emi_image_alt || "No Cost EMI"}
            width={254}
            height={84}
          />
        </div>
      ) : null}

      {hasEmi ? (
        <div className={variant === "desktop" ? "" : "mt-[52px]"}>
          <p className="text-[12px] font-normal italic leading-normal">
            {feeRow.meta_emi_prefix_label}
          </p>
          <p className="text-[20px] font-semibold italic leading-normal">
            {feeRow.meta_emi_amount}
          </p>
        </div>
      ) : null}

      {hasTotalFee ? (
        <div
          className={cn(
            variant === "desktop"
              ? "mt-10"
              : "mt-[50px] flex flex-col items-center justify-center",
          )}
        >
          <p
            className={cn(
              variant === "desktop"
                ? "text-[16px] font-light italic leading-normal"
                : "text-[21px] font-semibold",
            )}
          >
            {feeRow.meta_total_fee_label || "Total Fee"}
          </p>
          <div
            className={cn(
              variant === "desktop"
                ? "flex items-center gap-2.5"
                : "flex flex-col items-center",
            )}
          >
            {feeRow.meta_markup_fee?.trim() ? (
              <p
                className={cn(
                  "line-through font-medium leading-normal",
                  variant === "desktop" ? "text-[22px]" : "mt-[18px] text-[26px]",
                )}
              >
                {feeRow.meta_markup_fee}
              </p>
            ) : null}
            <p
              className={cn(
                "font-bold leading-normal",
                variant === "desktop" ? "text-[39px]" : "mt-[5px] text-[37px]",
              )}
            >
              {feeRow.meta_total_fee_amount}
            </p>
          </div>
        </div>
      ) : null}
    </>
  );

  return (
    <section
      ref={ref}
      className="w-full flex items-center justify-center"
      style={{ backgroundColor: section.desktop_background_color || "#FFFFFF" }}
    >
      <div className="flex w-full flex-col items-center justify-center pt-[64px] pb-[42px] px-[24px] min-[768px]:py-[52px] min-[768px]:px-[60px] max-w-[1100px]">
        {/* Header */}
        <div className="flex flex-col items-center justify-center text-center">
          {/* Desktop title is capped to the section's own content column (663px
              — the same width as the notes/eligibility blocks below, and the
              fee-card row's 391 + 16 + 256), so a long title breaks where the
              reference CU layout breaks it. The outer shell is 980px wide and
              would otherwise keep the whole title on one line. */}
          {section.title ? (
            <p
              className="text-[24px] font-semibold min-[768px]:text-[32px] min-[768px]:max-w-[663px] text-center"
              style={{ color: section.title_start_color || "#171719" }}
              dangerouslySetInnerHTML={{
                __html: highlight(section.title, accent),
              }}
            />
          ) : null}

          {section.subtitle ? (
            <p
              className="mt-4 min-[768px]:mt-3 max-w-[640px] text-[14px] min-[768px]:text-[16px] leading-[22px] font-normal"
              style={{ color: section.subtitle_color || "#373737" }}
            >
              {section.subtitle}
            </p>
          ) : null}

          {section.button_text ? (
            <div className="mt-4 min-[768px]:mt-5 flex justify-center">
              {opensLeadPopup ? (
                <button
                  type="button"
                  onClick={handleApplyClick}
                  className="inline-flex items-center gap-2 rounded-md px-[18px] py-[12px] text-[15px] font-bold shadow-sm transition-opacity hover:opacity-95"
                  style={btnStyle}
                >
                  {section.button_text}
                  <ArrowUpRight className="size-5 shrink-0" aria-hidden />
                </button>
              ) : (
                <a
                  href={ctaHrefNormalized}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-md px-[18px] py-[12px] text-[15px] font-bold shadow-sm transition-opacity hover:opacity-95"
                  style={btnStyle}
                  onClick={() =>
                    ctaClick({
                      section: "program_benefits_fee_plan_6",
                      component: "cta_apply_link",
                      label: section.button_text?.trim() || "apply",
                      step: "1",
                      entity: "program_benefits_6",
                      parentEntity: "program_benefits",
                    })
                  }
                >
                  {section.button_text}
                  <ArrowUpRight className="size-5 shrink-0" aria-hidden />
                </a>
              )}
            </div>
          ) : null}

          {feeRow.meta_student_type?.trim() ? (
            <div className="mt-3 min-[768px]:mt-[24px]">
              <p className="text-[18px] min-[768px]:text-[19px] font-semibold min-[768px]:font-medium leading-[20px] text-black">
                {feeRow.meta_student_type}
              </p>
            </div>
          ) : null}
        </div>

        {/* Scholarship note */}
        {feeRow.meta_scholarship_note?.trim() ? (
          <div className="w-full text-center min-[768px]:w-[663px] min-[768px]:text-start mt-9 mb-[26px]">
            <p
              className="feeplan-scholarship text-[16px] min-[768px]:text-[18px] font-semibold"
              style={{
                backgroundImage: `linear-gradient(to left, ${accent} 0%, ${accent} 25%, #171719 50%, ${accent} 75%, ${accent} 100%)`,
              }}
            >
              {feeRow.meta_scholarship_note}
            </p>
          </div>
        ) : null}

        {/* Fee plan toggle — shown only when 2+ plans exist */}
        {showPlanToggle ? (
          <div className="w-full flex justify-center mb-6">
            <div className="flex items-center bg-gray-100 rounded-full p-1">
              {feeRows.map((plan, index) => {
                const isActive = index === selectedPlan;
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setSelectedPlan(index)}
                    className={cn(
                      "px-4 py-2 rounded-full text-sm font-medium transition-colors duration-300",
                      isActive
                        ? "bg-white text-black shadow-md"
                        : "text-gray-600 hover:text-black",
                    )}
                  >
                    {plan.meta_plan_label?.trim() || `Plan ${index + 1}`}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        {/* Fee structure — desktop */}
        {hasFeeStructure ? (
          <div className="hidden min-[768px]:flex gap-4 mt-2">
            {hasTotalFee ? (
              <div
                className="w-[391px] h-[283px] relative pt-[54px] pb-[57px] pl-[38px] pr-[52px] text-white"
                style={{ background: feeCardGradient }}
              >
                {renderFeeCardInner("desktop")}
              </div>
            ) : null}

            {hasAmountBoxes ? (
              <div className="w-[256px] flex flex-col gap-3 justify-center">
                {hasSemester ? (
                  <div
                    className="flex flex-col justify-center h-[136px] text-white px-6 py-4"
                    style={{ backgroundColor: amountBoxBg }}
                  >
                    <span className="text-[16px] font-medium">
                      {feeRow.meta_semester_label || "Per Semester"}
                    </span>
                    <div className="text-[32px] font-semibold">
                      {feeRow.meta_semester_amount}
                    </div>
                  </div>
                ) : null}
                {hasAnnual ? (
                  <div
                    className="flex flex-col justify-center h-[136px] text-white px-6 py-4"
                    style={{ backgroundColor: amountBoxBg }}
                  >
                    <span className="text-[16px] font-medium">
                      {feeRow.meta_annual_label || "Per Annum"}
                    </span>
                    <div className="text-[32px] font-semibold">
                      {feeRow.meta_annual_amount}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Fee structure — mobile */}
        {hasFeeStructure ? (
          <div className="flex flex-col items-center w-[270px] gap-4 min-[768px]:hidden mt-2">
            {hasSemester ? (
              <div
                className="flex flex-col items-center justify-center text-white w-full p-4"
                style={{ backgroundColor: amountBoxBg }}
              >
                <p className="text-[16px] font-normal">
                  {feeRow.meta_semester_label || "Per Semester"}
                </p>
                <p className="text-[28px] font-semibold">
                  {feeRow.meta_semester_amount}
                </p>
              </div>
            ) : null}
            {hasAnnual ? (
              <div
                className="flex flex-col items-center justify-center text-white w-full p-4"
                style={{ backgroundColor: amountBoxBg }}
              >
                <p className="text-[16px] font-normal">
                  {feeRow.meta_annual_label || "Per Annum"}
                </p>
                <p className="text-[28px] font-semibold">
                  {feeRow.meta_annual_amount}
                </p>
              </div>
            ) : null}
            {hasTotalFee ? (
              <div
                className="h-[347px] flex flex-col justify-center items-center relative w-full text-white"
                style={{ background: feeCardGradient }}
              >
                {renderFeeCardInner("mobile")}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Notes */}
        {notes.length > 0 ? (
          <div className="w-[270px] min-[768px]:w-[663px] mt-8 text-sm text-start">
            {notes.map((note, index) => (
              <p
                key={index}
                className="text-[14px] font-medium leading-[22px] text-black"
                dangerouslySetInnerHTML={{ __html: highlight(note, accent) }}
              />
            ))}
          </div>
        ) : null}

        {/* Eligibility */}
        {feeRow.meta_eligibility?.trim() ? (
          <div
            className="w-[270px] min-[768px]:w-[663px] flex justify-start items-center mt-6 border py-[16px] px-[26px] min-[768px]:p-[18px] rounded-[8px]"
            style={{
              backgroundColor: "#FEF2F2",
              borderColor: "#C6C6C6",
            }}
          >
            <div
              className="fee-plan-eligibility text-[14px] min-[768px]:text-[16px] font-semibold leading-[22px] text-black text-start"
              dangerouslySetInnerHTML={{
                __html: isHtml(feeRow.meta_eligibility)
                  ? feeRow.meta_eligibility
                  : highlight(feeRow.meta_eligibility, accent),
              }}
            />
          </div>
        ) : null}

        {/* Certificates accordion */}
        {showCertificates ? (
          <div className="w-[270px] min-[768px]:w-[663px] mt-12 min-[768px]:mt-16">
            {section.pre_title?.trim() ? (
              <div className="text-center mb-8 min-[768px]:mb-12">
                <h2
                  className="text-[24px] min-[768px]:text-[36px] font-semibold text-black mb-3"
                  style={{ color: section.title_start_color || "#171719" }}
                  dangerouslySetInnerHTML={{
                    __html: highlight(section.pre_title, accent),
                  }}
                />
                {section.description?.trim() ? (
                  <p className="text-[14px] min-[768px]:text-[18px] text-black/60">
                    {section.description}
                  </p>
                ) : null}
              </div>
            ) : null}

            <div className="w-full space-y-4">
              {certificateGroups.map((group, groupIndex) => {
                const isSingleItem = certificateGroups.length === 1;
                const isOpen = isSingleItem ? true : openAccordionIndex === groupIndex;
                const currentIndex = carouselIndices[groupIndex] || 0;
                const total = group.images.length;

                return (
                  <div key={groupIndex} className="rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() => !isSingleItem && toggleAccordion(groupIndex)}
                      className={cn(
                        "w-full flex items-center justify-between gap-3 px-4 min-[768px]:px-6 py-4 min-[768px]:py-5 border border-gray-200 text-left transition-all duration-300",
                        isOpen
                          ? "text-white"
                          : "bg-gray-50 text-black hover:bg-gray-100",
                        isSingleItem ? "cursor-default" : "cursor-pointer",
                      )}
                      style={
                        isOpen
                          ? { backgroundColor: amountBoxBg, borderColor: amountBoxBg }
                          : undefined
                      }
                      disabled={isSingleItem}
                    >
                      <span className="text-[16px] min-[768px]:text-[20px] font-semibold">
                        {group.title}
                      </span>
                      {!isSingleItem ? (
                        <ChevronDown
                          className={cn(
                            "w-5 h-5 min-[768px]:w-6 min-[768px]:h-6 shrink-0 transition-transform duration-300",
                            isOpen ? "rotate-180 text-white" : "text-gray-600",
                          )}
                        />
                      ) : null}
                    </button>

                    <div
                      className={cn(
                        "transition-all duration-300 ease-in-out overflow-hidden",
                        isOpen ? "max-h-[800px] opacity-100" : "max-h-0 opacity-0",
                      )}
                    >
                      {total > 0 ? (
                        <div className="p-4 min-[768px]:p-8 bg-gradient-to-br from-red-50 to-white">
                          <div className="relative flex items-center justify-center">
                            {total > 1 ? (
                              <button
                                type="button"
                                onClick={() => handlePrevCertificate(groupIndex, total)}
                                className="absolute left-0 min-[768px]:left-4 z-10 w-10 h-10 min-[768px]:w-12 min-[768px]:h-12 flex items-center justify-center text-white rounded-full shadow-lg transition-opacity hover:opacity-90"
                                style={{ backgroundColor: amountBoxBg }}
                                aria-label="Previous certificate"
                              >
                                <ChevronLeft className="w-5 h-5 min-[768px]:w-6 min-[768px]:h-6" />
                              </button>
                            ) : null}

                            <div
                              className={cn(
                                "w-full max-w-[280px] min-[768px]:max-w-[600px] mx-auto",
                                total > 1 ? "px-12 min-[768px]:px-20" : "px-4 min-[768px]:px-8",
                              )}
                            >
                              <div className="relative aspect-[4/3] w-full">
                                {group.images[currentIndex] ? (
                                  <Image
                                    src={group.images[currentIndex].src}
                                    alt={group.images[currentIndex].alt}
                                    fill
                                    className="object-contain"
                                  />
                                ) : null}
                              </div>
                            </div>

                            {total > 1 ? (
                              <button
                                type="button"
                                onClick={() => handleNextCertificate(groupIndex, total)}
                                className="absolute right-0 min-[768px]:right-4 z-10 w-10 h-10 min-[768px]:w-12 min-[768px]:h-12 flex items-center justify-center text-white rounded-full shadow-lg transition-opacity hover:opacity-90"
                                style={{ backgroundColor: amountBoxBg }}
                                aria-label="Next certificate"
                              >
                                <ChevronRight className="w-5 h-5 min-[768px]:w-6 min-[768px]:h-6" />
                              </button>
                            ) : null}
                          </div>

                          {total > 1 ? (
                            <div className="flex items-center justify-center gap-2 mt-6 min-[768px]:mt-8">
                              {group.images.map((_, index) => (
                                <button
                                  key={index}
                                  type="button"
                                  onClick={() =>
                                    setCarouselIndices((prev) => ({
                                      ...prev,
                                      [groupIndex]: index,
                                    }))
                                  }
                                  className={cn(
                                    "h-2 rounded-full transition-all duration-300",
                                    index === currentIndex
                                      ? "w-6"
                                      : "w-2 bg-gray-300 hover:bg-gray-400",
                                  )}
                                  style={
                                    index === currentIndex
                                      ? { backgroundColor: amountBoxBg }
                                      : undefined
                                  }
                                  aria-label={`Go to certificate ${index + 1}`}
                                />
                              ))}
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
